import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Building2, Mail, Lock, User, Phone, Gift, Fingerprint } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google "G" SVG icon
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
    <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 2.58z" fill="#EA4335"/>
  </svg>
);

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register, setAuthData } = useAuth();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEmail, setBiometricEmail] = useState('');

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'agent',
    referral_code: ''
  });

  // Auto-fill email from previous session and trigger biometric check
  useEffect(() => {
    const lastEmail = localStorage.getItem('last_login_email');
    if (lastEmail && !loginData.email) {
      setLoginData(prev => ({ ...prev, email: lastEmail }));
    }
  }, []);

  // Check if biometric login is available when email changes
  useEffect(() => {
    const checkBiometric = async () => {
      if (!loginData.email || !loginData.email.includes('@')) {
        setBiometricAvailable(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/webauthn/check-available?email=${encodeURIComponent(loginData.email)}`);
        setBiometricAvailable(res.data.available);
        if (res.data.available) setBiometricEmail(loginData.email);
      } catch {
        setBiometricAvailable(false);
      }
    };

    const timeout = setTimeout(checkBiometric, 500);
    return () => clearTimeout(timeout);
  }, [loginData.email]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(loginData.email, loginData.password);
    setLoading(false);
    
    if (result.success) {
      localStorage.setItem('last_login_email', loginData.email);
      toast.success(t('auth.loginSuccess'));
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await register(registerData);
    setLoading(false);
    
    if (result.success) {
      toast.success(t('auth.registerSuccess'));
      setRegisterData({ name: '', email: '', phone: '', password: '', role: 'agent' });
    } else {
      toast.error(result.error);
    }
  };

  const handleGoogleLogin = () => {
    const clientId = "581564893206-uqadd986q60p3eah6ep105ljmfiq617d.apps.googleusercontent.com";
    const redirectUri = window.location.origin + '/auth/callback';
    const scope = "openid email profile";
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}`;
    window.location.href = googleAuthUrl;
  };

  const handleBiometricLogin = async () => {
    if (!biometricEmail) {
      toast.error('Enter your email first to use biometric login');
      return;
    }

    // Check WebAuthn browser support
    if (!window.PublicKeyCredential) {
      toast.error('Biometric login is not supported on this browser');
      return;
    }

    setLoading(true);
    try {
      // 1. Get authentication options from server
      const optionsRes = await axios.post(`${API}/auth/webauthn/login-options`, {
        email: biometricEmail
      });
      const options = optionsRes.data;
      const challengeId = options.challenge_id;

      // 2. Convert base64url to ArrayBuffer for WebAuthn API
      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        rpId: window.location.hostname,
        timeout: 60000,
        userVerification: 'required',
        allowCredentials: (options.allowCredentials || []).map(cred => ({
          id: base64urlToBuffer(cred.id),
          type: cred.type,
          transports: cred.transports || ['internal'],
        })),
      };

      // 3. Call browser WebAuthn API (triggers Face ID / Fingerprint)
      const assertion = await navigator.credentials.get({
        publicKey: publicKeyOptions
      });

      // 4. Convert response to JSON-serializable format
      const credentialData = {
        id: bufferToBase64url(assertion.rawId),
        rawId: bufferToBase64url(assertion.rawId),
        type: assertion.type,
        response: {
          authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
          clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
          signature: bufferToBase64url(assertion.response.signature),
          userHandle: assertion.response.userHandle ? bufferToBase64url(assertion.response.userHandle) : null,
        },
        authenticatorAttachment: assertion.authenticatorAttachment,
        rp_id: window.location.hostname,
        origin: window.location.origin,
      };

      // 5. Verify with backend
      const verifyRes = await axios.post(`${API}/auth/webauthn/login-verify`, {
        credential: credentialData,
        challenge_id: challengeId
      });

      const { access_token, user: userData } = verifyRes.data;
      setAuthData(access_token, userData);
      toast.success('Biometric login successful!');
      navigate('/dashboard');
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        toast.error('Biometric authentication was cancelled');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Biometric login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen relative overflow-hidden ${isDark ? 'bg-[#141c26]' : 'bg-[#EDE8F5]'}`}>
      {/* Abstract Real Estate Background */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/9558aa03-e535-4a2d-a616-2f456d356f32/images/79ecbb70670d51de20261579fb8de2f9cdf5dd84294a9aa6c24c7b2bf12278fd.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          filter: isDark ? 'none' : 'invert(0.92) sepia(0.1) hue-rotate(200deg) brightness(1.2)',
        }}
      >
        <div className={`absolute inset-0 ${
          isDark
            ? 'bg-gradient-to-t from-[#141c26]/90 via-[#141c26]/40 to-[#141c26]/70'
            : 'bg-gradient-to-t from-[#EDE8F5]/95 via-[#EDE8F5]/70 to-[#EDE8F5]/90'
        }`} />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo/Header */}
          <div className={`text-center mb-8 px-6 py-4 rounded-2xl ${
            isDark ? '' : 'backdrop-blur-md bg-white/50'
          }`}>
            <div className="flex items-center justify-center gap-3 mb-3">
              <h1 className={`text-4xl font-serif font-bold tracking-tight ${isDark ? 'text-white' : 'text-[#1a202c]'}`}
                style={{ textShadow: isDark ? '0 2px 8px rgba(0,0,0,0.5)' : 'none' }}
              >
                Property<span className="text-[var(--brand)]">Shop</span>
              </h1>
            </div>
            <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-[#4A5568]'}`}
              style={{ textShadow: isDark ? '0 1px 4px rgba(0,0,0,0.5)' : 'none' }}
            >{t('auth.loginSubtitle')}</p>
          </div>

          <Card className={`shadow-2xl border ${
            isDark
              ? 'glass-card border-white/5'
              : 'bg-white/90 backdrop-blur-xl border-[#ADBBDA]/30'
          }`}>
            <CardHeader className="space-y-1">
              <CardTitle className={`text-2xl font-serif ${isDark ? 'text-white' : 'text-[#2D3748]'}`}>{t('auth.loginTitle')}</CardTitle>
              <CardDescription className={isDark ? 'text-gray-400' : 'text-[#8697C4]'}>
                {t('auth.noAccount')} {t('auth.registerTitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className={`grid w-full grid-cols-2 mb-6 ${isDark ? 'bg-[#2E3944]' : 'bg-[#ADBBDA]/30'}`}>
                  <TabsTrigger 
                    value="login" 
                    data-testid="login-tab"
                    className={`data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white ${!isDark ? 'data-[state=inactive]:text-[#4A5568]' : ''}`}
                  >
                    {t('auth.login')}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register" 
                    data-testid="register-tab"
                    className={`data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white ${!isDark ? 'data-[state=inactive]:text-[#4A5568]' : ''}`}
                  >
                    {t('auth.register')}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className={isDark ? 'text-gray-300' : 'text-[#2D3748]'}>{t('auth.email')}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="login-email"
                          data-testid="login-email-input"
                          type="email"
                          placeholder={t('auth.enterEmail')}
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          className={`pl-10 focus:border-[var(--brand)] ${
                            isDark
                              ? 'bg-[#212A31] border-white/10 text-white'
                              : 'bg-white border-[#ADBBDA]/40 text-[#2D3748] placeholder:text-gray-400'
                          }`}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className={isDark ? 'text-gray-300' : 'text-[#2D3748]'}>{t('auth.password')}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="login-password"
                          data-testid="login-password-input"
                          type="password"
                          placeholder={t('auth.enterPassword')}
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          className={`pl-10 focus:border-[var(--brand)] ${
                            isDark
                              ? 'bg-[#212A31] border-white/10 text-white'
                              : 'bg-white border-[#ADBBDA]/40 text-[#2D3748] placeholder:text-gray-400'
                          }`}
                          required
                        />
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        data-testid="login-submit-button"
                        className="flex-1 btn-gold text-black font-semibold"
                        disabled={loading}
                      >
                        {loading ? t('common.loading') : t('auth.login')}
                      </Button>
                      {biometricAvailable && (
                        <Button
                          type="button"
                          data-testid="biometric-login-button"
                          onClick={handleBiometricLogin}
                          disabled={loading}
                          className={`px-4 ${
                            isDark
                              ? 'bg-[#2E3944] hover:bg-[#3a4a58] text-white border border-white/10'
                              : 'bg-[#ADBBDA]/20 hover:bg-[#ADBBDA]/30 text-[#2D3748] border border-[#ADBBDA]/40'
                          }`}
                          title="Login with Face ID / Fingerprint"
                        >
                          <Fingerprint className="w-5 h-5" />
                        </Button>
                      )}
                    </div>

                    <div className="text-center mt-2">
                      <Link 
                        to="/forgot-password" 
                        data-testid="forgot-password-link"
                        className={`text-sm hover:text-[var(--brand)] transition-colors ${isDark ? 'text-gray-400' : 'text-[#8697C4]'}`}
                      >
                        {t('auth.forgotPassword')}
                      </Link>
                    </div>

                    {/* Divider */}
                    <div className="relative my-4">
                      <Separator className={isDark ? 'bg-white/10' : 'bg-[#ADBBDA]/30'} />
                      <span className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-3 text-xs ${
                        isDark ? 'text-gray-500 bg-[var(--card-bg,#1a2530)]' : 'text-[#8697C4] bg-white'
                      }`}>
                        or continue with
                      </span>
                    </div>

                    {/* Google Sign In */}
                    <Button
                      type="button"
                      data-testid="google-login-button"
                      onClick={handleGoogleLogin}
                      className="w-full bg-white hover:bg-gray-100 text-gray-800 font-medium flex items-center justify-center gap-3 h-11 transition-all"
                      disabled={loading}
                    >
                      <GoogleIcon />
                      Sign in with Google
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className={isDark ? 'text-gray-300' : 'text-[#2D3748]'}>{t('auth.name')}</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="register-name"
                          data-testid="register-name-input"
                          type="text"
                          placeholder={t('auth.name')}
                          value={registerData.name}
                          onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                          className={`pl-10 focus:border-[var(--brand)] ${
                            isDark
                              ? 'bg-[#212A31] border-white/10 text-white'
                              : 'bg-white border-[#ADBBDA]/40 text-[#2D3748] placeholder:text-gray-400'
                          }`}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email" className={isDark ? 'text-gray-300' : 'text-[#2D3748]'}>{t('auth.email')}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="register-email"
                          data-testid="register-email-input"
                          type="email"
                          placeholder={t('auth.enterEmail')}
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          className={`pl-10 focus:border-[var(--brand)] ${
                            isDark
                              ? 'bg-[#212A31] border-white/10 text-white'
                              : 'bg-white border-[#ADBBDA]/40 text-[#2D3748] placeholder:text-gray-400'
                          }`}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-phone" className={isDark ? 'text-gray-300' : 'text-[#2D3748]'}>{t('auth.phone')}</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="register-phone"
                          data-testid="register-phone-input"
                          type="tel"
                          placeholder={t('auth.phone')}
                          value={registerData.phone}
                          onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                          className={`pl-10 focus:border-[var(--brand)] ${
                            isDark
                              ? 'bg-[#212A31] border-white/10 text-white'
                              : 'bg-white border-[#ADBBDA]/40 text-[#2D3748] placeholder:text-gray-400'
                          }`}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password" className={isDark ? 'text-gray-300' : 'text-[#2D3748]'}>{t('auth.password')}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="register-password"
                          data-testid="register-password-input"
                          type="password"
                          placeholder={t('auth.createPassword')}
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          className={`pl-10 focus:border-[var(--brand)] ${
                            isDark
                              ? 'bg-[#212A31] border-white/10 text-white'
                              : 'bg-white border-[#ADBBDA]/40 text-[#2D3748] placeholder:text-gray-400'
                          }`}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-referral" className={isDark ? 'text-gray-300' : 'text-[#2D3748]'}>Referral Code <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-[#8697C4]'}`}>(optional)</span></Label>
                      <div className="relative">
                        <Gift className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="register-referral"
                          data-testid="register-referral-input"
                          type="text"
                          placeholder="Enter referral code"
                          value={registerData.referral_code}
                          onChange={(e) => setRegisterData({ ...registerData, referral_code: e.target.value.toUpperCase() })}
                          className={`pl-10 focus:border-[var(--brand)] uppercase ${
                            isDark
                              ? 'bg-[#212A31] border-white/10 text-white'
                              : 'bg-white border-[#ADBBDA]/40 text-[#2D3748] placeholder:text-gray-400'
                          }`}
                          maxLength={8}
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      data-testid="register-submit-button"
                      className="w-full btn-gold text-black font-semibold"
                      disabled={loading}
                    >
                      {loading ? t('common.loading') : t('auth.register')}
                    </Button>

                    {/* Divider */}
                    <div className="relative my-4">
                      <Separator className={isDark ? 'bg-white/10' : 'bg-[#ADBBDA]/30'} />
                      <span className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-3 text-xs ${
                        isDark ? 'text-gray-500 bg-[var(--card-bg,#1a2530)]' : 'text-[#8697C4] bg-white'
                      }`}>
                        or
                      </span>
                    </div>

                    {/* Google Sign Up */}
                    <Button
                      type="button"
                      data-testid="google-register-button"
                      onClick={handleGoogleLogin}
                      className="w-full bg-white hover:bg-gray-100 text-gray-800 font-medium flex items-center justify-center gap-3 h-11 transition-all"
                      disabled={loading}
                    >
                      <GoogleIcon />
                      Sign up with Google
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

// WebAuthn helper: base64url string to ArrayBuffer
function base64urlToBuffer(base64url) {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return buffer;
}

// WebAuthn helper: ArrayBuffer to base64url string
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
